import axios, { type AxiosInstance } from "axios";
import {
  v2RequestSnakeCaseTransformer,
  v2ResponseCamelCaseTransformer,
} from "mws-utils/lib/caseTransformers/axiosCaseTransformers";
import { dateTransformer } from "mws-utils/lib/dateTransformer";

import { platformKey } from "~/constants/headerKeys";
import { getAnalyticsHeaders } from "~/utils/analytics";

export default defineNuxtPlugin({
  dependsOn: ["platform", "amplify"],
  setup: () => {
    // we are adding an adapter to axios to make it work with fetch so that it works in the Cloudflare workers
    axios.defaults.adapter = axiosFetchAdapter;

    setAxiosInterceptors(axios);
  },
});

function setAxiosInterceptors(axios: AxiosInstance) {
  const auth = useAuth();
  const platformStore = usePlatformStore();
  const nuxtApp = useNuxtApp();

  axios.defaults.transformResponse = [dateTransformer];
  axios.interceptors.request.use(async (req) => {
    const transformedReq = v2RequestSnakeCaseTransformer(req);

    transformedReq.headers.Authorization = await auth.getToken();
    transformedReq.headers[platformKey] =
      platformStore.tryGetPlatformConfiguration?.platformId;

    const { analyticsContext, headers } = getAnalyticsHeaders(nuxtApp);

    transformedReq.headers = {
      ...transformedReq.headers,
      trackingContext: JSON.stringify(analyticsContext),
      // Backwards compatibility with the old tracking headers FAN-1917
      ...headers,
    };

    return transformedReq;
  });

  axios.interceptors.response.use(v2ResponseCamelCaseTransformer, (error) => {
    const err = error;
    if ("response" in err) {
      err.response = v2ResponseCamelCaseTransformer(err.response);
    }

    if (err.response?.status === 404) {
      throw createError({
        statusCode: 404,
        statusMessage: "404 page not found",
        message: "404 page not found",
      });
    }

    throw err;
  });
}
